import Vue from 'vue'
import Vuex from 'vuex'
import API from '@/api/index.js'
import OctUsers from '@/store/modules/OctUsers.js'
import OctCustomers from '@/store/modules/OctCustomers.js'
import OctItems from '@/store/modules/OctItems.js'
import OctProducts from '@/store/modules/OctProducts.js'
import OctComments from '@/store/modules/OctComments.js'
import OctNews from '@/store/modules/OctNews.js'
import OctSalons from '@/store/modules/OctSalons.js'
import updateNotificationPlugin from '@/store/plugins/updateNotificationPlugin.js'

Vue.use(Vuex)

// Session key
const storageKey = '@@stylestockspajs@@'

/** Load session data from session storage */
const loadSessionData = () => (
  JSON.parse(sessionStorage.getItem(storageKey) || '{}')
)

export default new Vuex.Store({
  state: {
    /** Salon ID */
    id: '',
    /** Salon name */
    name: '',
    profileIcon: {
      id: '',
      lowResolution: '',
      standardResolution: '',
      highResolution: '',
    },
    /** Salon's menus for treatment */
    menus: [
      {
        id: 1,
        title: 'カット',
        slug: 'cut',
      },
      {
        id: 2,
        title: 'カラー',
        slug: 'color',
      },
      {
        id: 3,
        title: 'パーマ',
        slug: 'perm',
      },
      {
        id: 4,
        title: 'ストレートパーマ',
        slug: 'straight-perm',
      },
      {
        id: 5,
        title: 'エクステ',
        slug: 'hair-extensions',
      },
      {
        id: 7,
        title: 'その他アレンジ',
        slug: 'other-arrange',
      },
      {
        id: 8,
        title: 'ヘッドスパ',
        slug: 'head-spa',
      },
      {
        id: 9,
        title: 'まつげエクステ',
        slug: 'eyelash-extensions',
      },
      {
        id: 10,
        title: 'まつげパーマ',
        slug: 'eyelash-perm',
      },
    ],
    /** Salon's status */
    status: '',
    /** Reservation URL */
    reservationUrl: null,
    /**
     * Extra data
     */
    data: {
      /**
       * Secret customers (100 items maximum)
       * @property {string} {}.id - Customer ID
       */
      secretCustomers: []
    },
    /** Indicate downloading data from API */
    processing: false,
    /** Error */
    error: null,
    /** Alert */
    alert: null,
    /** Confirmation */
    confirmation: null,
    /** Notification */
    notification: null,
    /** Indicate whether online or not */
    isOnline: navigator ? navigator.onLine : true,
    /** Session data */
    session: loadSessionData()
  },
  getters: {
    /** @returns Whether the current salon is a management salon or not */
    isManagement: state => state.id === '12000010',
    /** @returns Using iPad */
    isIPad () {
      // For iPadOS 13 or later
      if (navigator.userAgent.includes("Mac") && "ontouchend" in document) {
        return true;
      }
      // iPadOS 12 or earlier
      return navigator.userAgent.includes("iPad");
    },
    /** @returns Whether the customer is set as secret */
    isSecretCustomer: state => customerId => state.data.secretCustomers.some(item => item.id === customerId),
    /** @returns Whether current salon has beta access */
    hasBetaAccess: state => /(12000001|00000001|salonB|salonT)/.test(state.id)
  },
  actions: {
    /*
    * Fetch salon
    * @param {string} id Salon ID to fetch
    */
    async fetch ({commit, state}, id=state.id) {
      return API.getSalon(id).then(response => {
        // Set default icon
        if (!response.profileIcon ||  !response.profileIcon.id)
          Object.assign(response.profileIcon, {
            standardResolution: '/images/salon__default.png',
            lowResolution: '/images/salon__default.png',
          })

        // Initialize response.data if it is not an object
        if (!response.data || typeof response.data !== 'object')
          response.data = {}

        // Initialize response.data.secretCustomers if it is not an array
        if (!response.data.secretCustomers || !Array.isArray(response.data.secretCustomers))
          response.data.secretCustomers = []

        // Update state and returns response
        commit('state', response)
        return Promise.resolve(response)
      })
    },

    /**
     * Save salon
     * @param {object} payload - Payload to save
     * @param {string} payload.reservationUrl - Reservation URL
     * @returns {Promise<object>} Response
     * @throws {Error} - Throws Error if the response is not ok
     */
    async save ({state, commit}, payload) {
      return API.updateSalon(state.id, payload).then(response => {
        commit('state', {
          reservationUrl: response.reservationUrl
        })
        return Promise.resolve(response)
      })
    },

    /** Alert message */
    async alert ({commit}, message) {
      commit('state', {alert: message})
      alert(message)
      return Promise.resolve()
    },

    /** Notify message */
    async notify ({commit}, message) {
      commit('state', {notification: message})
      return Promise.resolve()
    },

    /** Confirm message */
    async confirm ({commit}, message) {
      return new Promise ((resolve, reject) => {
        commit('state', {
          confirmation: {
            message: message.text ? message.text : message,
            resolve: resolve,
            reject: reject
          }
        })
      })
    },

    /**
     * Updates the online status in the store and notifies the user if offline.
     *
     * @param {boolean} val - The new online status. If false, triggers a danger notification.
     */
    setOnlineStatus ({ commit, dispatch }, val) {
      !val && dispatch('notify', {text: 'ネットワーク接続が切れました。', type: 'danger'})
      commit('isOnline', val);
    },

    /**
     * Initializes listeners for the browser's online and offline events.
     * Dispatches the 'setOnlineStatus' action with the current online status
     * whenever the network connectivity changes, and immediately on initialization.
     */
    initOnlineStatusListener ({ dispatch }) {
      window.addEventListener('online', () => dispatch('setOnlineStatus', true))
      window.addEventListener('offline', () => dispatch('setOnlineStatus', false))
      dispatch('setOnlineStatus', navigator.onLine);
    },

    /**
     * Update salon data
     * @param {object} payload - Payload to update
     * @returns {Promise<object>} Response
     * @throws {FetchError} - Throws FetchError if the response is not ok
     */
    async updateSalonData ({state, commit}, payload) {
      return API.updateSalonData(state.id, payload).then(response => {
        commit('state', {data: response})
        return response
      })
    }
  },
  mutations: {
    /** Error */
    error (state, thrown) {
      state.error = thrown
    },

    /** Session data */
    session (state, val) {
      Vue.set(state, 'session', val)
      // Store in session storage
      sessionStorage.setItem(storageKey, JSON.stringify(
        Object.assign(loadSessionData(), val)))
    },

    /** Set online status */
    isOnline (state, val) {
      state.isOnline = val;
    },

    /** Set state */
    state (state, val) {
      Object.assign(state, val)
    }
  },
  modules: {
    /** Customers */
    customers: OctCustomers,
    /** Products */
    products: OctProducts,
    /** Treatments */
    treatments: OctItems,
    /** All staffs */
    staffs: OctUsers,
    /** Salon’s comments */
    comments: OctComments,
    /** News */
    news: OctNews,
    /** Salons */
    salons: OctSalons
  },
  plugins: [
    updateNotificationPlugin()
  ]
})
